import { createFileRoute } from '@tanstack/react-router';
import { isAirAdmin } from '@airreading/common';
import {
  availabilityOverviewSearchSchema,
  AvailabilityOverviewDay,
  AvailabilityOverviewDistrict,
  AvailabilityOverviewSeasonTotal,
  AvailabilityOverviewTimeZone,
  AvailabilityOverviewTimeslot,
  BackgroundCheckStatusLabelMap,
  ClassRatio,
  classRatioSchema,
  CoachAvailabilityType,
  CoachStatus,
  CoachSubStatus,
  coachStatusSchema,
  DayOfWeekLabelMap,
  GetAvailabilityOverviewResults,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Info } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ArrowTooltip } from '@/components/arrow-tooltip';
import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPage } from '@/components/info-page';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterDropdownSubItems } from '@/components/table/table-filter-dropdown-sub-items';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { TableFilterTimeRangePicker } from '@/components/table/table-filter-time-range-picker';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu';
import { buildBooleanSelectOptions } from '@/components/ui/multi-select-utils';
import { getAvailabilityOverview } from '@/lib/api-calls';
import {
  classEligibilityOptions,
  classRatioOptions,
  availabilityOverviewTimeSlotOptions,
  coachStatusGroupedOptions,
  getCoachOverallGradeOptions,
  teachingDaysOptions,
} from '@/lib/coach-utils';
import { useErrorToast } from '@/lib/error-handling';
import { booleanFilterToSelectedValues, selectedValuesToBooleanFilter, usePaginatedFilters } from '@/lib/filtering';
import { paginationRequestSchema } from '@/lib/pagination';
import { validateRouteRole } from '@/lib/routing';
import { coachesSectionTabs } from '../-components/tabs';
import {
  AvailabilityOverviewDownloadCsv,
  formatAvailabilityTimeslotRange,
  formatTimeZoneLabel,
} from './-components/availability-overview-download-csv';
import { TeacherStateFilterDropdown } from '../-components/teacher-state-filter-dropdown';
import { z } from 'zod';

export const Route = createFileRoute('/_layout/teachers/availability-overview/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: AvailabilityOverview,
  validateSearch: paginationRequestSchema.extend({
    ...availabilityOverviewSearchSchema.shape,
    googleMeetTechCheck: z.coerce.boolean().optional(),
    statuses: z.array(coachStatusSchema).default([CoachStatus.ready_to_teach]),
    classRatio: classRatioSchema.default(ClassRatio['1:2']),
  }),
});
const timeslotColumnHelper = createColumnHelper<AvailabilityOverviewTimeslot>();

const timeslotColumns = [
  timeslotColumnHelper.accessor('startPlainTime', {
    id: 'timeslot',
    header: 'Timeslots',
    enableSorting: false,
    cell: ({ getValue }) => formatAvailabilityTimeslotRange(getValue()),
  }),
  timeslotColumnHelper.accessor('studentSeats', {
    id: 'studentSeats',
    header: 'Student seats',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  timeslotColumnHelper.accessor('enrolledStudentsNeedSchedule', {
    id: 'enrolledStudentsNeedSchedule',
    header: 'Enrolled students need schedule',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  timeslotColumnHelper.accessor('teacherDemandForecast', {
    id: 'teacherDemandForecast',
    header: 'Teacher demand forecast',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  timeslotColumnHelper.accessor('coachCount', {
    id: 'coachCount',
    header: 'Available teachers',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
];

const dayColumnHelper = createColumnHelper<AvailabilityOverviewDay>();

const dayColumns = [
  dayColumnHelper.accessor('day', {
    id: 'day',
    header: 'Days of the week',
    enableSorting: false,
    cell: ({ getValue }) => DayOfWeekLabelMap[getValue()].abbr,
  }),
  dayColumnHelper.accessor('studentSeats', {
    id: 'studentSeats',
    header: 'Student seats',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  dayColumnHelper.accessor('enrolledStudentsNeedSchedule', {
    id: 'enrolledStudentsNeedSchedule',
    header: 'Enrolled students need schedule',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  dayColumnHelper.accessor('teacherDemandForecast', {
    id: 'teacherDemandForecast',
    header: 'Teacher demand forecast',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  dayColumnHelper.accessor('coachCount', {
    id: 'coachCount',
    header: 'Available teachers',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
];

const timeZoneColumnHelper = createColumnHelper<AvailabilityOverviewTimeZone>();

const timeZoneColumns = [
  timeZoneColumnHelper.accessor('timeZone', {
    id: 'timeZone',
    header: 'Time zones',
    enableSorting: false,
    cell: ({ getValue }) => formatTimeZoneLabel(getValue()),
  }),
  timeZoneColumnHelper.accessor('coachCount', {
    id: 'coachCount',
    header: 'Available teachers',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
];

const seasonTotalColumnHelper = createColumnHelper<AvailabilityOverviewSeasonTotal>();

const seasonTotalColumns = [
  seasonTotalColumnHelper.accessor('cycle', {
    id: 'cycle',
    header: 'Cycles',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  seasonTotalColumnHelper.accessor('coachCount', {
    id: 'coachCount',
    header: 'Available teachers',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
];

const districtColumnHelper = createColumnHelper<AvailabilityOverviewDistrict>();

const districtColumns = [
  districtColumnHelper.accessor('district', {
    id: 'district',
    header: 'District',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  districtColumnHelper.accessor('studentSeats', {
    id: 'studentSeats',
    header: 'Student seats',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  districtColumnHelper.accessor('enrolledStudentsNeedSchedule', {
    id: 'enrolledStudentsNeedSchedule',
    header: 'Enrolled students need schedule',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  districtColumnHelper.accessor('teacherDemandForecast', {
    id: 'teacherDemandForecast',
    header: 'Teacher demand forecast',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  districtColumnHelper.accessor('coachCount', {
    id: 'coachCount',
    header: 'Available teachers',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
];

const AvailabilityOverviewTimeslotsCard = ({
  timeslots,
  isLoading,
  hasError,
  refetch,
}: {
  timeslots: AvailabilityOverviewTimeslot[];
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}) => {
  const table = useReactTable({
    data: timeslots,
    columns: timeslotColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <InfoCard
      headerText="Availability by time slot"
      isLoading={isLoading}
      contentClass="block max-h-[420px] overflow-auto"
      data-testid="availability-overview-timeslots"
    >
      <EntityTable
        table={table}
        isLoading={isLoading}
        hasError={hasError}
        emptyState="No time slots found"
        emptyStateClassName="translate-y-0"
        errorState={<TableErrorState refetchAction={refetch} />}
        data-testid="availability-overview-timeslots-table"
      />
    </InfoCard>
  );
};

const AvailabilityOverviewDaysCard = ({
  daysOfWeek,
  isLoading,
  hasError,
  refetch,
}: {
  daysOfWeek: AvailabilityOverviewDay[];
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}) => {
  const table = useReactTable({
    data: daysOfWeek,
    columns: dayColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <InfoCard
      headerText="Availability by class days"
      isLoading={isLoading}
      contentClass="block"
      data-testid="availability-overview-days"
    >
      <EntityTable
        table={table}
        isLoading={isLoading}
        hasError={hasError}
        emptyState="No days found"
        emptyStateClassName="translate-y-0"
        errorState={<TableErrorState refetchAction={refetch} />}
        data-testid="availability-overview-days-table"
      />
    </InfoCard>
  );
};

const AvailabilityOverviewBackgroundChecksCard = ({
  backgroundCheckStatuses,
  isLoading,
}: Pick<GetAvailabilityOverviewResults, 'backgroundCheckStatuses'> & { isLoading: boolean }) => {
  return (
    <InfoCard
      headerText="Available teachers per background check"
      isLoading={isLoading}
      maxItemsPerRow={4}
      data-testid="availability-overview-background-checks"
    >
      {backgroundCheckStatuses.map((item) => (
        <InfoKeyValue
          key={item.status}
          label={BackgroundCheckStatusLabelMap[item.status]}
          value={item.coachCount}
          data-testid={`availability-overview-background-check-${item.status}`}
        />
      ))}
    </InfoCard>
  );
};

const AvailabilityOverviewDistrictsCard = ({
  districts,
  isLoading,
  hasError,
  refetch,
}: {
  districts: AvailabilityOverviewDistrict[];
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}) => {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'enrolledStudentsNeedSchedule', desc: true }]);

  const table = useReactTable({
    data: districts,
    columns: districtColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <InfoCard
      headerText="Availability by districts"
      isLoading={isLoading}
      contentClass="block max-h-[420px] overflow-auto"
      data-testid="availability-overview-districts"
    >
      <EntityTable
        table={table}
        isLoading={isLoading}
        hasError={hasError}
        emptyState="No districts found"
        emptyStateClassName="translate-y-0"
        errorState={<TableErrorState refetchAction={refetch} />}
        data-testid="availability-overview-districts-table"
      />
    </InfoCard>
  );
};

const AvailabilityOverviewTimeZonesCard = ({
  timeZones,
  isLoading,
  hasError,
  refetch,
}: {
  timeZones: AvailabilityOverviewTimeZone[];
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}) => {
  const table = useReactTable({
    data: timeZones,
    columns: timeZoneColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <InfoCard
      headerText="Available teachers by time zone"
      isLoading={isLoading}
      contentClass="block max-h-[420px] overflow-auto"
      data-testid="availability-overview-time-zones"
    >
      <EntityTable
        table={table}
        isLoading={isLoading}
        hasError={hasError}
        emptyState="No time zones found"
        emptyStateClassName="translate-y-0"
        errorState={<TableErrorState refetchAction={refetch} />}
        data-testid="availability-overview-time-zones-table"
      />
    </InfoCard>
  );
};

const AvailabilityOverviewSeasonTotalCard = ({
  seasonTotals,
  isLoading,
}: {
  seasonTotals: AvailabilityOverviewSeasonTotal[];
  isLoading: boolean;
}) => {
  const table = useReactTable({
    data: seasonTotals,
    columns: seasonTotalColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <InfoCard
      headerText={
        <span className="flex items-center gap-2">
          Season total available teachers
          <ArrowTooltip trigger={<Info className="h-5 w-5 m-1" />} position="right">
            Filters don't apply to this table
          </ArrowTooltip>
        </span>
      }
      isLoading={isLoading}
      contentClass="block"
      data-testid="availability-overview-season-total"
    >
      <EntityTable
        table={table}
        isLoading={isLoading}
        emptyState="No cycles found"
        emptyStateClassName="translate-y-0"
        data-testid="availability-overview-season-total-table"
        hideEndMessage
      />
    </InfoCard>
  );
};

function AvailabilityOverview() {
  const {
    resetFilters,
    activeFiltersCount,
    filters: {
      statuses: [[statuses, activeStatuses], setStatuses],
      subStatuses: [[subStatuses, activeSubStatuses], setSubStatuses],
      overallGrades: [[overallGrades, activeOverallGrades], setOverallGrades],
      classEligibilities: [[classEligibilities, activeClassEligibilities], setClassEligibilities],
      teachingDays: [[teachingDays, activeTeachingDays], setTeachingDays],
      timeRanges: [[timeRanges], setTimeRanges],
      untakenTeachersOnly: [[untakenTeachersOnly], setUntakenTeachersOnly],
      coachAvailabilityType: [[coachAvailabilityType], setCoachAvailabilityType],
      googleMeetTechCheck: [[googleMeetTechCheck, activeGoogleMeetTechCheck], setGoogleMeetTechCheck],
      startOnOrAfter: [[startOnOrAfter], setStartOnOrAfter],
      endOnOrBefore: [[endOnOrBefore], setEndOnOrBefore],
      classRatio: [[classRatio, activeClassRatio], setClassRatio],
      timeSlots: [[timeSlots, activeTimeSlots], setTimeSlots],
      states: [[states, activeStates], setStates],
    },
  } = usePaginatedFilters(
    Route.id,
    [
      'statuses',
      'subStatuses',
      'overallGrades',
      'classEligibilities',
      'teachingDays',
      'timeRanges',
      'untakenTeachersOnly',
      'coachAvailabilityType',
      'googleMeetTechCheck',
      'startOnOrAfter',
      'endOnOrBefore',
      'classRatio',
      'timeSlots',
      'states',
    ],
    ['overallGrades', 'googleMeetTechCheck', 'classRatio', 'timeSlots', 'states'],
  );

  const queryModifier = useMemo(
    () => ({
      filters: {
        ...(activeStatuses?.length ? { statuses: activeStatuses } : {}),
        ...(activeSubStatuses?.length ? { subStatuses: activeSubStatuses } : {}),
        ...(activeOverallGrades?.length ? { overallGrades: activeOverallGrades } : {}),
        ...(activeClassEligibilities?.length ? { classEligibilities: activeClassEligibilities } : {}),
        ...(activeTeachingDays?.length ? { teachingDays: activeTeachingDays } : {}),
        ...(timeRanges?.length
          ? {
              timeRanges,
              untakenTeachersOnly: untakenTeachersOnly ?? true,
              coachAvailabilityType: coachAvailabilityType ?? CoachAvailabilityType.regular,
            }
          : {}),
        googleMeetTechCheck: activeGoogleMeetTechCheck ?? googleMeetTechCheck,
        ...(startOnOrAfter ? { startOnOrAfter } : {}),
        ...(endOnOrBefore ? { endOnOrBefore } : {}),
        ...(activeClassRatio ? { classRatio: activeClassRatio } : {}),
        ...(activeTimeSlots?.length ? { timeSlots: activeTimeSlots } : {}),
        ...(activeStates?.length ? { states: activeStates } : {}),
      },
    }),
    [
      activeStatuses,
      activeSubStatuses,
      activeOverallGrades,
      activeClassEligibilities,
      activeTeachingDays,
      timeRanges,
      untakenTeachersOnly,
      coachAvailabilityType,
      googleMeetTechCheck,
      activeGoogleMeetTechCheck,
      startOnOrAfter,
      endOnOrBefore,
      activeClassRatio,
      activeTimeSlots,
      activeStates,
    ],
  );
  const { data, isPending, error, refetch } = useQuery(getAvailabilityOverview(queryModifier));

  useErrorToast(error, 'Error loading availability overview. Please try again.');

  const timeslots = data?.timeslots ?? [];
  const daysOfWeek = data?.daysOfWeek ?? [];
  const backgroundCheckStatuses = data?.backgroundCheckStatuses ?? [];
  const districts = data?.districts ?? [];
  const timeZones = data?.timeZones ?? [];
  const seasonTotals = data?.seasonTotals ?? [];
  const hasError = !!error;

  const filterComponents = (
    <div className="flex flex-wrap items-center gap-2 pb-4 pt-3">
      <TableFilterDropdownSubItems<CoachStatus, CoachSubStatus>
        title="Status"
        options={coachStatusGroupedOptions}
        selectedValues={statuses ?? []}
        selectedSubValues={subStatuses ?? []}
        onChange={setStatuses}
        onSubValuesChange={setSubStatuses}
      />
      <TableFilterPlainDateRangePicker
        fromPlainDateString={startOnOrAfter}
        toPlainDateString={endOnOrBefore}
        onRangeSelect={(range) => {
          setStartOnOrAfter(range.fromPlainDateString);
          setEndOnOrBefore(range.toPlainDateString);
        }}
        placeholder="Date range"
      />
      <TableFilterDropdown
        title="Teaching days"
        options={teachingDaysOptions}
        selectedValues={teachingDays ?? []}
        onChange={setTeachingDays}
      />
      <TableFilterTimeRangePicker
        placeholder="Time range"
        selectedRanges={timeRanges ?? []}
        onChange={setTimeRanges}
        untakenTeachersOnly={untakenTeachersOnly ?? true}
        onUntakenTeachersOnlyChange={setUntakenTeachersOnly}
        coachAvailabilityType={coachAvailabilityType ?? CoachAvailabilityType.regular}
        onCoachAvailabilityTypeChange={setCoachAvailabilityType}
      />
      <TableFilterDropdown
        title="Class Eligibility"
        options={classEligibilityOptions}
        selectedValues={classEligibilities ?? []}
        onChange={setClassEligibilities}
      />
      <TableMoreFiltersMenu
        activeFiltersCount={activeFiltersCount}
        filterItems={[
          {
            title: 'Google Meet Tech Check',
            filterComponent: (
              <TableFilterDropdown
                title="Google Meet Tech Check"
                options={buildBooleanSelectOptions()}
                variant="radio"
                selectedValues={booleanFilterToSelectedValues(googleMeetTechCheck)}
                onChange={(vals) => setGoogleMeetTechCheck(selectedValuesToBooleanFilter(vals))}
              />
            ),
          },
          {
            title: 'Time slots',
            filterComponent: (
              <TableFilterDropdown
                title="Time slots"
                options={availabilityOverviewTimeSlotOptions}
                selectedValues={timeSlots ?? []}
                onChange={setTimeSlots}
              />
            ),
          },
          {
            title: 'Overall Grade',
            filterComponent: (
              <TableFilterDropdown
                title="Overall Grade"
                options={getCoachOverallGradeOptions()}
                selectedValues={overallGrades ?? []}
                onChange={setOverallGrades}
              />
            ),
          },
          {
            title: 'Class ratio',
            filterComponent: (
              <TableFilterDropdown
                title="Class ratio"
                options={classRatioOptions}
                variant="radio"
                selectedValues={classRatio ? [classRatio] : []}
                onChange={(vals) => setClassRatio(vals[0])}
              />
            ),
          },
          {
            title: 'State',
            filterComponent: <TeacherStateFilterDropdown selectedValues={states} onChange={setStates} />,
          },
        ]}
      />
      <TableFilterResetButton show={true} onClick={resetFilters} />
      <AvailabilityOverviewDownloadCsv
        data={{ timeslots, daysOfWeek, backgroundCheckStatuses, districts, timeZones, seasonTotals }}
        disabled={isPending || hasError}
      />
    </div>
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      {filterComponents}
      <AvailabilityOverviewTimeslotsCard
        timeslots={timeslots}
        isLoading={isPending}
        hasError={hasError}
        refetch={refetch}
      />
      <AvailabilityOverviewDaysCard
        daysOfWeek={daysOfWeek}
        isLoading={isPending}
        hasError={hasError}
        refetch={refetch}
      />
      <AvailabilityOverviewBackgroundChecksCard
        backgroundCheckStatuses={backgroundCheckStatuses}
        isLoading={isPending}
      />
      <AvailabilityOverviewDistrictsCard
        districts={districts}
        isLoading={isPending}
        hasError={hasError}
        refetch={refetch}
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <AvailabilityOverviewTimeZonesCard
          timeZones={timeZones}
          isLoading={isPending}
          hasError={hasError}
          refetch={refetch}
        />
        <AvailabilityOverviewSeasonTotalCard seasonTotals={seasonTotals} isLoading={isPending} />
      </div>
    </InfoPage>
  );
}
